'use client'

import React from 'react'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { cn, warnIfEmptyLabel } from '@/utils'

export const createSkeletonShimmer = () =>
  cn(
    'animate-shimmer bg-90 bg-size-[400%] bg-[linear-gradient(90deg,_var(--color-gray-200)_30%,_var(--color-gray-300)_50%,_var(--color-gray-200)_70%)]',
  )

const skeletonVariants = cva(createSkeletonShimmer(), {
  variants: {
    variant: {
      rectangle: 'rounded w-full h-4',
      circle: 'rounded-full w-12 h-12',
    },
  },
  defaultVariants: {
    variant: 'rectangle',
  },
})

export type SkeletonProps = {
  /**
   * Shape of the placeholder: `rectangle` fills the available width, `circle`
   * renders a 48px round placeholder. Defaults to `rectangle`.
   */
  variant?: VariantProps<typeof skeletonVariants>['variant']
  /** Overrides the width from `variant`. Numbers are treated as pixels. */
  width?: string | number
  /** Overrides the height from `variant`. Numbers are treated as pixels. */
  height?: string | number
  /**
   * Optional accessible name. Omit for decorative placeholders (the default —
   * the skeleton is hidden from assistive tech). Provide a localized string
   * only when this skeleton represents a single, meaningful loading region,
   * turning it into a `role="status"` live region.
   */
  ariaLabel?: string
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'aria-label'>

export const Skeleton = ({
  className,
  variant = 'rectangle',
  width,
  height,
  style,
  ariaLabel,
  ...props
}: SkeletonProps) => {
  if (ariaLabel !== undefined) {
    warnIfEmptyLabel(ariaLabel, 'Skeleton: ariaLabel')
  }

  const statusProps =
    ariaLabel !== undefined
      ? { role: 'status' as const, 'aria-label': ariaLabel }
      : { 'aria-hidden': true }

  return (
    <div
      className={cn(skeletonVariants({ variant }), className)}
      style={{
        width: width,
        height: height,
        ...style,
      }}
      {...statusProps}
      {...props}
    />
  )
}
